import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Groups as GroupsIcon,
  ManageAccounts as AccountsIcon,
  Message as LiveIcon,
  Archive as MessagesIcon,
  Contacts as LeadsIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { whatsappAccountService } from '../services/whatsappAccountService';
import { adminService } from '../services/adminService';

const statusColor = (account: { isReady: boolean; isConnected: boolean }) => {
  if (account.isReady) return 'success';
  if (account.isConnected) return 'warning';
  return 'error';
};

const statusLabel = (account: { isReady: boolean; isConnected: boolean }) => {
  if (account.isReady) return 'Ready';
  if (account.isConnected) return 'Connected';
  return 'Offline';
};

const StatusIcon = ({ account }: { account: { isReady: boolean; isConnected: boolean } }) => {
  if (account.isReady) return <CheckIcon color="success" fontSize="small" />;
  if (account.isConnected) return <WarningIcon color="warning" fontSize="small" />;
  return <ErrorIcon color="error" fontSize="small" />;
};

const QuickLink: React.FC<{ icon: React.ReactNode; label: string; to: string }> = ({ icon, label, to }) => {
  const navigate = useNavigate();
  return (
    <Button
      variant="outlined"
      startIcon={icon}
      onClick={() => navigate(to)}
      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 500 }}
      fullWidth
    >
      {label}
    </Button>
  );
};

const WhatsappOverview: React.FC = () => {
  const navigate = useNavigate();

  const { data: accounts, isLoading: accountsLoading } = useQuery({
    queryKey: ['whatsappAccounts'],
    queryFn: whatsappAccountService.getAccounts,
    refetchInterval: 15_000,
  });

  const { data: health, isLoading: healthLoading } = useQuery({
    queryKey: ['operationsHealth'],
    queryFn: adminService.getOperationsHealth,
    refetchInterval: 30_000,
  });

  const ready = accounts?.filter((a) => a.isReady).length ?? 0;
  const connected = accounts?.filter((a) => a.isConnected && !a.isReady).length ?? 0;
  const offline = accounts?.filter((a) => !a.isConnected).length ?? 0;
  const totalGroups = accounts?.reduce((sum, a) => sum + (a.groups?.length ?? 0), 0) ?? 0;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        WhatsApp Overview
      </Typography>

      {/* KPI row */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: 'Ready accounts', value: ready, color: 'success.main' },
          { label: 'Connected (QR)', value: connected, color: 'warning.main' },
          { label: 'Offline', value: offline, color: 'error.main' },
          { label: 'Known groups', value: health?.whatsapp.knownGroupCount ?? totalGroups, color: 'text.primary' },
          { label: 'Monitored groups', value: health?.whatsapp.monitoredGroupCount ?? 0, color: 'primary.main' },
        ].map(({ label, value, color }) => (
          <Grid item xs={6} sm={4} md={2.4} key={label}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="h4" fontWeight={700} color={color}>
                {accountsLoading || healthLoading ? '–' : value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Account status list */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Account Status</Typography>
              <Button size="small" onClick={() => navigate('/whatsapp/accounts')}>
                Manage accounts →
              </Button>
            </Box>

            {accountsLoading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={28} />
              </Box>
            ) : !accounts?.length ? (
              <Alert severity="info">
                No WhatsApp accounts configured.{' '}
                <Button size="small" onClick={() => navigate('/whatsapp/accounts')}>Add one</Button>
              </Alert>
            ) : (
              <Stack spacing={1.5}>
                {accounts.map((account) => (
                  <Box
                    key={account._id}
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                    p={1.5}
                    border="1px solid"
                    borderColor="divider"
                    borderRadius={1}
                  >
                    <StatusIcon account={account} />
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {account.accountName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {account.phoneNumber || 'No phone'}
                      </Typography>
                    </Box>
                    <Chip
                      label={statusLabel(account)}
                      color={statusColor(account) as any}
                      size="small"
                    />
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <GroupsIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {account.groups?.length ?? 0}
                      </Typography>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* Quick links + message stats */}
        <Grid item xs={12} md={5}>
          <Stack spacing={2}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Quick Links</Typography>
              <Stack spacing={1}>
                <QuickLink icon={<AccountsIcon fontSize="small" />} label="Manage accounts" to="/whatsapp/accounts" />
                <QuickLink icon={<LiveIcon fontSize="small" />} label="Live messages" to="/whatsapp/live" />
                <QuickLink icon={<MessagesIcon fontSize="small" />} label="Message archive" to="/whatsapp/messages" />
                <QuickLink icon={<LeadsIcon fontSize="small" />} label="Leads" to="/whatsapp/leads" />
              </Stack>
            </Paper>

            {health && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>AI Queue</Typography>
                <Stack spacing={1}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Queue size</Typography>
                    <Typography variant="body2" fontWeight={600}>{health.aiQueue.queueSize}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">AI processing</Typography>
                    <Chip
                      size="small"
                      label={health.aiQueue.aiEnabled ? 'Enabled' : 'Paused'}
                      color={health.aiQueue.aiEnabled ? 'success' : 'warning'}
                    />
                  </Box>
                  {health.aiQueue.filterStats && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">Pre-filter efficiency</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {health.aiQueue.filterStats.filterEfficiency ?? 0}%
                      </Typography>
                    </Box>
                  )}
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Active leads today</Typography>
                    <Typography variant="body2" fontWeight={600}>{health.rides.ridesToday}</Typography>
                  </Box>
                </Stack>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mt: 2 }}
                  startIcon={<WhatsAppIcon fontSize="small" />}
                  onClick={() => navigate('/operations/ai')}
                >
                  AI Controls
                </Button>
              </Paper>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WhatsappOverview;
